import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Button from "@/components/ui/button";
import Tooltip from "@/components/ui/tooltip";
import type { MinimalTag } from "@/modules/tag/types";
import type { BrandKit } from "@/modules/brand/types";
import type { FormStatus } from "@/modules/form/types";
import { useFormBuilderState, type TrackedChanges } from "./hooks/use-form-builder-state";
import { useDirtyState } from "./hooks/use-dirty-state";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";
import FormBuilderLeftPanel from "./form-builder-left-panel";
import FormBuilderPreview from "./form-builder-preview";
import { ValidationErrorsTooltip } from "./validation-errors-tooltip";
import FormStatusChangeConfirmationModal from "../form-status-change-confirmation-modal";
import FormPublishedLinkModal from "../form-published-link-modal";
import type { FlowSection, Question } from "./types";
import Container from "@/components/ui/container";
import IconLinkChain from "@/components/icons/icon-link-chain";
import IconArrowLeftLane from "@/components/icons/icon-arrow-left-lane";
import { copyToClipboard } from "@/lib/utils";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { useAuth } from "@/modules/auth";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface TagSearchResult {
	tags: MinimalTag[];
	hasMore: boolean;
	nextCursor: string | null;
}

interface FormBuilderProps {
	formTitle?: string;
	formSlug?: string;
	brandName?: string;
	brandLogo?: string | null;
	brandKit?: BrandKit | null;
	tags?: MinimalTag[];
	initialSections?: FlowSection[];
	initialQuestions?: Question[];
	initialAutoApprove?: boolean;
	initialSelectedTagIds?: string[];
	initialTheme?: "light" | "dark" | "system-default";
	initialStatus?: FormStatus;
	onSave?: (data: {
		formTitle: string;
		sections: FlowSection[];
		questions: Question[];
		autoApproveTestimonials: boolean;
		selectedTagIds: string[];
		theme: "light" | "dark" | "system-default";
		status: FormStatus;
		trackedChanges: TrackedChanges;
	}) => Promise<void>;
	isLoading?: boolean;
	backUrl?: string;
	onCreateTag?: (label: string, onCreated: (tagId: string) => void) => void;
	hasMoreTags?: boolean;
	isLoadingMoreTags?: boolean;
	onLoadMoreTags?: () => void;
	onSearchTags?: (query: string, cursor?: string | null) => Promise<TagSearchResult | null>;
	onTagSelect?: (tag: MinimalTag) => void;
}

export default function FormBuilder({
	formTitle = "Feedback Collection Form",
	formSlug = "",
	brandName = "Your Brand",
	brandLogo = null,
	brandKit = null,
	tags = [],
	initialSections,
	initialQuestions,
	initialAutoApprove,
	initialSelectedTagIds,
	initialTheme,
	initialStatus = "unpublished",
	onSave,
	isLoading = false,
	backUrl,
	onCreateTag,
	hasMoreTags = false,
	isLoadingMoreTags = false,
	onLoadMoreTags,
	onSearchTags,
	onTagSelect,
}: FormBuilderProps) {
	const { currentWorkspaceId } = useAuth();
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const [isTitleFocused, setIsTitleFocused] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Modal states
	const [showPauseConfirmModal, setShowPauseConfirmModal] = useState(false);
	const [showPublishedLinkModal, setShowPublishedLinkModal] = useState(false);
	const [linkCopied, setLinkCopied] = useState(false);

	// Navigation hooks
	const navigate = useNavigate();
	const { confirmNavigation } = useNavigationGuard();

	// Generate form URL
	const formUrl = `${import.meta.env.VITE_CLIENT_FORMS_URL}/f/${formSlug}`;

	// State to force re-render when system preference changes
	const [, forceUpdate] = useState({});

	const formState = useFormBuilderState({
		initialFormTitle: formTitle,
		initialSections,
		initialQuestions,
		initialAutoApprove,
		initialSelectedTagIds,
		initialTheme,
		initialStatus,
	});

	// Set up dirty state tracking and navigation guards
	useDirtyState({ hasUnsavedChanges: formState.hasUnsavedChanges });

	// Compute effective theme mode
	const isDarkMode = useMemo(() => {
		if (formState.theme === "system-default") {
			return window.matchMedia("(prefers-color-scheme: dark)").matches;
		}
		return formState.theme === "dark";
	}, [formState.theme]);

	// Listen for system preference changes
	useEffect(() => {
		if (formState.theme !== "system-default") return;

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = () => forceUpdate({});
		mediaQuery.addEventListener("change", handler);
		return () => mediaQuery.removeEventListener("change", handler);
	}, [formState.theme]);

	// Track if we're unpublishing (status changing from published to unpublished)
	const isUnpublishing = formState.trackedChanges.formSettings.statusChanged && formState.status === "unpublished";

	// Handle save
	const handleSave = useCallback(async () => {
		// Block save if validation errors exist
		if (formState.hasValidationErrors) {
			const firstErrorId = formState.getFirstErrorSectionId();
			if (firstErrorId) {
				formState.selectSection(firstErrorId);
			}
			return;
		}

		if (!formState.hasUnsavedChanges || !onSave) return;

		// Clear any existing timeout
		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setSaveStatus("saving");

		// Check if we're publishing (status changing to published)
		const wasPublishing = formState.trackedChanges.formSettings.statusChanged && formState.status === "published";

		try {
			await onSave({
				...formState.getCurrentState(),
				trackedChanges: formState.trackedChanges,
			});
			formState.commitChanges();
			setSaveStatus("saved");

			// Show published link modal if we just published
			if (wasPublishing) {
				setShowPublishedLinkModal(true);
			}

			// Hide "Saved" after 2 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 2000);
		} catch (error) {
			console.error("Failed to save form:", error);
			setSaveStatus("error");

			// Reset error status after 3 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 3000);
		}
	}, [formState, onSave]);

	// Handle clicking save when unpublishing - show confirmation first
	const handleSaveClick = useCallback(() => {
		if (isUnpublishing) {
			setShowPauseConfirmModal(true);
		} else {
			handleSave();
		}
	}, [isUnpublishing, handleSave]);

	// Handle confirming the unpublish action
	const handleConfirmUnpublish = useCallback(() => {
		setShowPauseConfirmModal(false);
		handleSave();
	}, [handleSave]);

	// Handle discard
	const handleDiscard = useCallback(() => {
		formState.discardChanges();
	}, [formState]);

	// Handle copy link
	const handleCopyLink = useCallback(async () => {
		if (!formSlug) return;
		try {
			await copyToClipboard(formUrl);
			if (currentWorkspaceId) useOnboardingStore.getState().completeStep(currentWorkspaceId, "share_form");
			setLinkCopied(true);
			setTimeout(() => setLinkCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy link:", err);
		}
	}, [formSlug, formUrl]);

	// Handle back button click with navigation guard
	const handleBackClick = useCallback(() => {
		if (backUrl && confirmNavigation()) {
			navigate(backUrl);
		}
	}, [backUrl, confirmNavigation, navigate]);

	const isDiscardDisabled = !formState.hasUnsavedChanges || saveStatus === "saving" || saveStatus === "saved" || isLoading;
	const isSaveDisabled = isDiscardDisabled || formState.hasValidationErrors;

	return (
		<div className="relative flex h-screen bg-[#fafafa]">
			{/* Left Column - Sidebar Panel */}
			<div className="w-100 shrink-0 flex flex-col h-screen relative z-30 bg-white border-r border-gray-200">
				{/* Sidebar Header */}
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center gap-3">
						{backUrl && (
							<Button variant="soft" className="p-2" onClick={handleBackClick}>
								<IconArrowLeftLane className="w-5 h-5" />
							</Button>
						)}
						<div className="flex-1 min-w-0">
							{isTitleFocused ? (
								<input
									ref={titleInputRef}
									type="text"
									value={formState.formTitle}
									onChange={(e) => formState.setFormTitle(e.target.value)}
									onBlur={() => setIsTitleFocused(false)}
									autoComplete="off"
									data-1p-ignore
									data-lpignore="true"
									data-bwignore
									data-form-type="other"
									name="form-title"
									className="font-heading text-base font-semibold bg-transparent border-none outline-none rounded px-1 -ml-1 w-full text-gray-900 hover:outline-1 hover:outline-gray-300 focus:outline-1 focus:outline-gray-400"
								/>
							) : (
								<h3
									onClick={() => {
										setIsTitleFocused(true);
										setTimeout(() => titleInputRef.current?.focus(), 0);
									}}
									className="text-base font-semibold truncate cursor-text rounded px-1 -ml-1 w-full text-gray-900 hover:outline-1 hover:outline-gray-300 focus:outline-1 focus:outline-gray-400"
								>
									{formState.formTitle}
								</h3>
							)}
						</div>
					</div>
				</div>

				{/* Left Panel Content - Scrollable */}
				<div className="flex-1 overflow-y-auto">
					<FormBuilderLeftPanel
						formState={formState}
						tags={tags}
						isLoading={isLoading}
						onCreateTag={onCreateTag}
						hasMoreTags={hasMoreTags}
						isLoadingMoreTags={isLoadingMoreTags}
						onLoadMoreTags={onLoadMoreTags}
						onSearchTags={onSearchTags}
						onTagSelect={onTagSelect}
					/>
				</div>
			</div>

			{/* Sticky header with Status - Offset by sidebar width */}
			<div className="fixed top-0 left-100 right-0 z-20 px-8 py-4 bg-white border-b border-gray-200">
				<Container>
					<div className="flex items-center justify-between gap-4">
						<p className="text-sm text-tertiary">
							{formState.trackedChanges.formSettings.statusChanged
								? formState.status === "published"
									? "Your form will be published after saving."
									: "Your form will be unpublished after saving."
								: formState.status === "published"
									? "Your form is live and accepting submissions."
									: "Your form is unpublished and not accepting submissions."}
						</p>
						<div className="flex items-center gap-3">
							{/* Status dropdown */}
							<DropdownMenu.Root>
								<DropdownMenu.Trigger asChild>
									<button
										type="button"
										className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors hover:bg-gray-100 border border-gray-200 ${
											formState.status === "published" ? "text-green-700" : "text-gray-600"
										}`}
									>
										<span className={`w-2 h-2 rounded-full ${formState.status === "published" ? "bg-green-500" : "bg-gray-400"}`} />
										{formState.status === "published" ? "Published" : "Unpublished"}
										<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
										</svg>
									</button>
								</DropdownMenu.Trigger>
								<DropdownMenu.Portal>
									<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50 min-w-[140px]" sideOffset={5}>
										<DropdownMenu.Item
											className="flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer outline-none hover:bg-gray-50"
											onSelect={() => formState.setStatus("published")}
										>
											<span className="text-gray-700">Published</span>
											{formState.status === "published" && (
												<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											)}
										</DropdownMenu.Item>
										<DropdownMenu.Item
											className="flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer outline-none hover:bg-gray-50"
											onSelect={() => formState.setStatus("unpublished")}
										>
											<span className="text-gray-700">Unpublished</span>
											{formState.status === "unpublished" && (
												<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											)}
										</DropdownMenu.Item>
									</DropdownMenu.Content>
								</DropdownMenu.Portal>
							</DropdownMenu.Root>
							{formState.status === "unpublished" ? (
								<Tooltip content={formState.status === "unpublished" ? "Link is only available when the form is published." : undefined}>
									<span>
										<Button variant="light" className="flex items-center gap-2 shrink-0" disabled={formState.status === "unpublished"} onClick={handleCopyLink}>
											{linkCopied ? (
												<>
													<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
													</svg>
													<span className="text-green-600">Copied!</span>
												</>
											) : (
												<>
													<IconLinkChain />
													Copy Link
												</>
											)}
										</Button>
									</span>
								</Tooltip>
							) : (
								<span>
									<Button variant="light" className="flex items-center gap-2 shrink-0" onClick={handleCopyLink}>
										{linkCopied ? (
											<>
												<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
												<span className="text-green-600">Copied!</span>
											</>
										) : (
											<>
												<IconLinkChain />
												Copy Link
											</>
										)}
									</Button>
								</span>
							)}
						</div>
					</div>
				</Container>
			</div>

			{/* Right Column - Live Preview */}
			<div className="flex-1 overflow-auto h-[calc(100vh-142px)] mt-17.75">
				<FormBuilderPreview formState={formState} brandName={brandName} brandLogo={brandLogo} brandKit={brandKit} isDarkMode={isDarkMode} />
			</div>

			{/* Sticky footer with Save/Discard buttons - Offset by sidebar width */}
			<div className="fixed bottom-0 left-100 right-0 z-20 px-8 py-4 bg-white border-t border-gray-200">
				<Container>
					<div className="flex items-center justify-end">
						<div className="flex items-center gap-3">
							{/* Validation errors tooltip */}
							{formState.hasValidationErrors && <ValidationErrorsTooltip errors={formState.validationErrors} onErrorClick={(sectionId) => formState.selectSection(sectionId)} />}

							{/* Status indicator */}
							{!isLoading && saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							{/* Discard button */}
							<Button variant="light" onClick={handleDiscard} disabled={isDiscardDisabled}>
								Discard
							</Button>

							{/* Save button */}
							<Button variant="default" onClick={handleSaveClick} disabled={isSaveDisabled}>
								{saveStatus === "saving" ? "Saving..." : "Save Changes"}
							</Button>
						</div>
					</div>
				</Container>
			</div>

			{/* Unpublish confirmation modal */}
			<FormStatusChangeConfirmationModal
				open={showPauseConfirmModal}
				onOpenChange={setShowPauseConfirmModal}
				currentStatus="published"
				newStatus="unpublished"
				onConfirm={handleConfirmUnpublish}
				isLoading={saveStatus === "saving"}
			/>

			{/* Published link modal */}
			<FormPublishedLinkModal open={showPublishedLinkModal} onOpenChange={setShowPublishedLinkModal} formTitle={formState.formTitle} link={formUrl} />
		</div>
	);
}
